import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';
import { Ethnicity, EthnicityNames } from '@/types/profile';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  flexRow: {
    flexDirection: "row",
    gap: 20,
    alignItems: "center",
    alignSelf: "center",
    flexWrap: "wrap",
  },
});

export default function EthnicityScreen() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const [ethnicityWhite, setEthnicityWhite] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_WHITE, communityMembership.profile.ethnicity));
  const [ethnicityBlack, setEthnicityBlack] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_BLACK, communityMembership.profile.ethnicity));
  const [ethnicityEastAsian, setEthnicityEastAsian] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_EAST_ASIAN, communityMembership.profile.ethnicity));
  const [ethnicityHispanic, setEthnicityHispanic] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_HISPANIC, communityMembership.profile.ethnicity));
  const [ethnicityMiddleEastern, setEthnicityMiddleEastern] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_MIDDLE_EASTERN, communityMembership.profile.ethnicity));
  const [ethnicityNativeAmerican, setEthnicityNativeAmerican] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_NATIVE_AMERICAN, communityMembership.profile.ethnicity));
  const [ethnicityPacificIslander, setEthnicityPacificIslander] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_PACIFIC_ISLANDER, communityMembership.profile.ethnicity));
  const [ethnicitySouthAsian, setEthnicitySouthAsian] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_SOUTH_ASIAN, communityMembership.profile.ethnicity));
  const [ethnicitySoutheastAsian, setEthnicitySoutheastAsian] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_SOUTHEAST_ASIAN, communityMembership.profile.ethnicity));
  const [ethnicityOther, setEthnicityOther] = useState(AppUtils.isBitSet(Ethnicity.ETHNICITY_OTHER, communityMembership.profile.ethnicity));

  const onSubmit = useCallback(
    async (skip: boolean) => {
      try {
        showSpinner();

        const response = await AppUtilities.fetchAuthorized(
          "EthnicityScreen.onSubmit",
          "/rabbl/api/optionalsettings",
          "POST",
          {
            domain: domain,
            ethnicities: 1,
            ethnicityWhite: ethnicityWhite,
            ethnicityBlack: ethnicityBlack,
            ethnicityEastAsian: ethnicityEastAsian,
            ethnicityHispanic: ethnicityHispanic,
            ethnicityMiddleEastern: ethnicityMiddleEastern,
            ethnicityNativeAmerican: ethnicityNativeAmerican,
            ethnicityPacificIslander: ethnicityPacificIslander,
            ethnicitySouthAsian: ethnicitySouthAsian,
            ethnicitySoutheastAsian: ethnicitySoutheastAsian,
            ethnicityOther: ethnicityOther,
          }
        );
        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        dispatch(setData(response));
        if (edit && edit.length > 0 && router.canGoBack()) {
          router.back();
        } else {
          router.navigate(AppUtils.getProfileCreateNextOptionalPageLink("ethnicity", membership) as RelativePathString);
        }

      } catch (e) {
        AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(skip), true);
        AppUtilities.handleException(e, "EthnicityScreen.onSubmit");
      } finally {
        hideSpinner();
      }
    },
    [
      router,
      domain,
      ethnicityWhite,
      ethnicityBlack,
      ethnicityEastAsian,
      ethnicityHispanic,
      ethnicityMiddleEastern,
      ethnicityNativeAmerican,
      ethnicityPacificIslander,
      ethnicitySouthAsian,
      ethnicitySoutheastAsian,
      ethnicityOther,
    ]
  );

  return (
    <BasicScrollView center={true}>
      <Meta title={"Ethnicity"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <CustomText>Your ethnicity</CustomText>

          <Checkbox
            status={ethnicityBlack ? 'checked' : 'unchecked'}
            onPress={() => setEthnicityBlack(ethnicityBlack ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_BLACK)}
          />

          <Checkbox
            status={ethnicityEastAsian ? 'checked' : 'unchecked'}
            onPress={() => setEthnicityEastAsian(ethnicityEastAsian ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_EAST_ASIAN)}
          />

          <Checkbox
            status={ethnicityHispanic ? 'checked' : 'unchecked'}
            onPress={() => setEthnicityHispanic(ethnicityHispanic ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_HISPANIC)}
          />

          <Checkbox
            status={ethnicityMiddleEastern ? 'checked' : 'unchecked'}
            onPress={() => setEthnicityMiddleEastern(ethnicityMiddleEastern ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_MIDDLE_EASTERN)}
          />

          <Checkbox
            status={ethnicityNativeAmerican ? 'checked' : 'unchecked'}
            onPress={() => setEthnicityNativeAmerican(ethnicityNativeAmerican ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_NATIVE_AMERICAN)}
          />

          <Checkbox
            status={ethnicityPacificIslander ? 'checked' : 'unchecked'}
            onPress={() => setEthnicityPacificIslander(ethnicityPacificIslander ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_PACIFIC_ISLANDER)}
          />

          <Checkbox
            status={ethnicitySouthAsian ? 'checked' : 'unchecked'}
            onPress={() => setEthnicitySouthAsian(ethnicitySouthAsian ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_SOUTH_ASIAN)}
          />

          <Checkbox
            status={ethnicitySoutheastAsian ? 'checked' : 'unchecked'}
            onPress={() => setEthnicitySoutheastAsian(ethnicitySoutheastAsian ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_SOUTHEAST_ASIAN)}
          />

          <Checkbox
            status={ethnicityWhite ? 'checked' : 'unchecked'}
            onPress={() => setEthnicityWhite(ethnicityWhite ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_WHITE)}
          />

          <Checkbox
            status={ethnicityOther ? 'checked' : 'unchecked'}
            onPress={() => setEthnicityOther(ethnicityOther ? false : true)}
            label={EthnicityNames.get(Ethnicity.ETHNICITY_OTHER)}
          />

          <View style={styles.flexRow}>
            <View style={styles.buttonWrapper}>
              <Button95
                onPress={() => onSubmit(false)}
              >{ edit ? "Save" : "Next" }</Button95>
            </View>

            {
              (!edit || edit.length == 0) && (
                <>
                  <View style={styles.buttonWrapper}>
                    <Button95
                      onPress={() => onSubmit(true)}
                    >Skip</Button95>
                  </View>
                  <View style={styles.buttonWrapper}>
                    <Button95
                      onPress={() => router.navigate(AppUtils.getProfileCreateNextPageLink(communityMembership) as RelativePathString)}
                    >Skip all</Button95>
                  </View>
                </>
              )
            }
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
